import React, { useState, useEffect, useRef } from 'react';
import './ActionButton.css';

const ActionButton = ({ 
  documentCount, 
  checklistProgress, 
  checklistTotal,
  onDocumentClick,
  onChecklistClick,
  onInvoiceClick,
  onCashflowClick,
  onLogsClick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  
  // Handle click on the main action button
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    // Close menu when escape key is pressed
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);
  
  // Calculate progress percentage for checklist
  const checklistPercentage = checklistTotal > 0 
    ? Math.round((checklistProgress / checklistTotal) * 100) 
    : 0;
  
  return (
    <div className="action-button-container" ref={containerRef}>
      {/* Main Action Button */}
      <button 
        className={`action-button ${isOpen ? 'active' : ''}`}
        onClick={toggleMenu}
        aria-label="Open action menu"
        aria-expanded={isOpen}
      >
        <div className="action-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
          </svg>
        </div>
        <span className="action-label">Actions</span>
      </button>
      
      {/* Menu Items */}
      {isOpen && (
        <div className="action-menu">
          {/* Document Button */}
          <button 
            className="menu-item documents-button"
            onClick={() => {
              onDocumentClick();
              setIsOpen(false);
            }}
            aria-label="Open documents panel"
          >
            <div className="menu-item-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 16h8v2H8v-2zm0-4h8v2H8v-2zm6-10H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" fill="currentColor"/>
              </svg>
            </div>
            <span className="menu-label">Docs</span>
            {documentCount > 0 && (
              <span className="menu-badge docs-badge">{documentCount}</span>
            )}
          </button>
          
          {/* Checklist Button */}
          <button 
            className="menu-item checklist-button"
            onClick={() => {
              onChecklistClick();
              setIsOpen(false);
            }}
            aria-label="Open checklist panel"
          >
            <div className="menu-item-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 5.18L10.59 16.6l-4.24-4.24 1.41-1.41 2.83 2.83 10-10L22 5.18zM19.79 10.22C19.92 10.79 20 11.39 20 12c0 4.42-3.58 8-8 8s-8-3.58-8-8c0-4.42 3.58-8 8-8 1.58 0 3.04.46 4.28 1.25l1.44-1.44C16.1 2.67 14.13 2 12 2 6.48 2 2 6.48 2 12c0 5.52 4.48 10 10 10s10-4.48 10-10c0-1.19-.22-2.33-.6-3.39l-1.61 1.61z" fill="currentColor"/>
              </svg>
            </div>
            <span className="menu-label">Checklist</span>
            {checklistTotal > 0 && (
              <div className="checklist-progress-indicator">
                <span className="checklist-progress-text">{checklistPercentage}%</span>
              </div>
            )}
          </button>
          
          {/* Finance Calculator Button */}
          <button 
            className="menu-item cashflow-button"
            onClick={() => {
              onCashflowClick();
              setIsOpen(false);
            }}
            aria-label="Open finance calculator"
          >
            <div className="menu-item-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" fill="currentColor"/>
              </svg>
            </div>
            <span className="menu-label">Finance</span>
          </button>
          
          {/* Invoice Button */}
          <button 
            className="menu-item invoice-button"
            onClick={() => {
              onInvoiceClick();
              setIsOpen(false);
            }}
            aria-label="Open invoice panel"
          >
            <div className="menu-item-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z" fill="currentColor"/>
                <path d="M8 14H16V16H8V14Z" fill="currentColor"/>
                <path d="M8 10H16V12H8V10Z" fill="currentColor"/>
                <path d="M8 18H12V20H8V18Z" fill="currentColor"/>
              </svg>
            </div>
            <span className="menu-label">Invoices</span>
          </button>
          
          {/* Delivery Logs Button */}
          {onLogsClick && (
            <button 
              className="menu-item logs-button"
              onClick={() => {
                onLogsClick();
                setIsOpen(false);
              }}
              aria-label="Open delivery logs"
            >
              <div className="menu-item-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" fill="currentColor"/>
                </svg>
              </div>
              <span className="menu-label">Logs</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionButton; 